"use strict";
import { jsx } from "react/jsx-runtime";
import LegacyRouter from "../util/legacy_router";
import App from "./app";
import Account from "../api/account";
import Auth from "../util/auth";
import ConfirmAndSubmitPage from "./confirm_and_submit_page";
import DocumentIndexerPage from "./document_indexer_page";
import FlowModeFindNextItemPage from "./flow_mode/flow_mode_find_next_item_page";
import EmailPage from "./email_page";
import FactorInvoiceExtractDataPage from "./factor_invoice_extract_data_page";
import LoginForm from "./login_form";
import Logout from "./logout";
import presenceService from "./presence_service_next_gen";
import ReplyView from "./reply_view";
import AnalyticsPage from "./analytics_page";
import RootPage from "./root_page";
import history, { locationDescriptor } from "./app_history";
import PineRoute from "../util/pine/pine_route";
import { readOnlyRedirect } from "./read_only";
import { carrierInvoiceWorkflowRedirect } from "./carrier_invoice_workflow";
import { factorInvoiceWorkflowWithEmail } from "./flow_mode/dashboard_paths";
const ElmRoute = PineRoute;
function externalApp(href) {
  return function ExternalApp() {
    window.location.href = href;
    return null;
  };
}
function requireAuth(nextState, replace) {
  var _a, _b, _c;
  const redirect = (path2, state = null) => replace(locationDescriptor(path2, null, state));
  let path;
  let oldLogin = false;
  if (nextState.location.pathname === "/" && nextState.location.hash.length > 1) {
    path = nextState.location.hash + nextState.location.search;
    if (path.substr(0, 7) === "#/login") {
      oldLogin = true;
    }
  } else {
    path = nextState.location.pathname + nextState.location.search;
  }
  path = path.replace(/^\/?#/, "");
  if (!Auth.isAuthenticated() && (path.substr(0, 6) !== "/login" || oldLogin)) {
    const next = oldLogin ? null : path;
    redirect("/login", { nextPathname: next });
    return;
  }
  if (isLogout(path)) {
    notifyPresenceServiceOfNavigation(path, nextState);
    return;
  }
  if (needsTermsAgreement(path) && path.substr(0, 6) !== "/terms") {
    redirect("/terms", { nextPathname: path });
    return;
  }
  if (path === "/terms") {
    notifyPresenceServiceOfNavigation(path, nextState);
    return;
  }
  if (customerRepOnly()) {
    redirect("/customer_representative", { nextPathname: path });
    return;
  }
  if (insightsOnly()) {
    redirect("/insights_dashboard", { nextPathname: path });
    return;
  }
  if (readOnlyFactorInvoices()) {
    const readOnlyRedirectPath = readOnlyRedirect(path);
    if (readOnlyRedirectPath) {
      redirect(readOnlyRedirectPath, { nextPathname: path });
    }
    return;
  }
  if (Account.useFactorInvoiceWorkflowPage() && !((_a = nextState.location.query) == null ? void 0 : _a.forceReact)) {
    const match = path.match(/\/factor_invoices\/(\d+)/);
    if (match) {
      const target = factorInvoiceWorkflowWithEmail(
        Number(match[1]),
        (_b = nextState.location.query) == null ? void 0 : _b.emailId,
        (_c = nextState.location.query) == null ? void 0 : _c.onDone
      );
      redirect(target.toString());
      return;
    }
  }
  if (Account.useCarrierInvoiceWorkflowPage()) {
    const carrierInvoiceWorkflowPath = carrierInvoiceWorkflowRedirect(path);
    if (carrierInvoiceWorkflowPath) {
      redirect(carrierInvoiceWorkflowPath);
      return;
    }
  }
  notifyPresenceServiceOfNavigation(path, nextState);
}
function readOnlyFactorInvoices() {
  const user = Auth._currentUser;
  return !!(user == null ? void 0 : user.read_only_factor_invoices);
}
function notifyPresenceServiceOfNavigation(path, nextState) {
  const searchParams = new URLSearchParams(nextState.location.search);
  searchParams.delete("onDone");
  const searchString = searchParams.toString();
  const pathName = nextState.location.pathname;
  presenceService.startIfNeeded(Auth._currentUser);
  presenceService.userMovedTo(
    "/app" + maybeShortenedPath(pathName) + (searchString ? "?" + searchString : "")
  );
}
function maybeShortenedPath(pathName) {
  const urlRegex = /^(\/index-document-batches\/\d+)\/factor_invoices\/\d+/i;
  const match = pathName.match(urlRegex);
  if (match) {
    return match[1];
  } else {
    return pathName;
  }
}
function needsTermsAgreement(path) {
  return !Auth._currentUser.terms_agreed_to_at;
}
function isLogout(path) {
  return path.substr(0, 7) === "/logout";
}
function customerRepOnly() {
  return Auth._currentUser && Auth._currentUser.accessible_apps.includes("customer_representative") && !Auth._currentUser.accessible_apps.includes("payables") && !Auth._currentUser.accessible_apps.includes("receivables");
}
function insightsOnly() {
  return Auth._currentUser && Auth._currentUser.accessible_apps.includes("insights_dashboard") && !Auth._currentUser.accessible_apps.includes("payables") && !Auth._currentUser.accessible_apps.includes("receivables");
}
export default function AppRouter() {
  const user = Auth.getCurrentUser();
  const settingsPath = user && user.account.forwarder_account ? "/settings/contacts" : "/settings/account";
  const routes = [
    { path: "/", component: RootPage, onEnter: requireAuth },
    { path: "/okta_login", component: ElmRoute },
    { path: "/help", component: ElmRoute, onEnter: requireAuth },
    { path: "/activity", component: ElmRoute, onEnter: requireAuth },
    {
      path: "/billing_requirements",
      component: ElmRoute,
      onEnter: requireAuth
    },
    { path: "/documents_viewer", component: ElmRoute, onEnter: requireAuth },
    { path: "/email_search", component: ElmRoute, onEnter: requireAuth },
    { path: "/elm*", component: ElmRoute, onEnter: requireAuth },
    { path: "/dashboard*", component: ElmRoute, onEnter: requireAuth },
    { path: "/to_be_scraped_loads", component: ElmRoute, onEnter: requireAuth },
    {
      path: "/index-document-batches/:batchId",
      component: DocumentIndexerPage,
      onEnter: requireAuth
    },
    {
      path: "/flow",
      component: FlowModeFindNextItemPage,
      onEnter: requireAuth
    },
    {
      path: "/carrier_invoices_workflow/:carrierInvoiceId",
      component: ElmRoute,
      onEnter: requireAuth
    },
    {
      path: "/factor_invoices_workflow/:factorInvoiceId",
      component: ElmRoute,
      onEnter: requireAuth
    },
    {
      path: "/factor_invoices_workflow/batch/:batchId",
      component: ElmRoute,
      onEnter: requireAuth
    },
    {
      path: "/factor_invoices/:factorInvoiceId",
      component: FactorInvoiceExtractDataPage,
      onEnter: requireAuth
    },
    {
      path: "/readonly_factor_invoices/:factorInvoiceId",
      component: ElmRoute,
      onEnter: requireAuth
    },
    {
      path: "/readonly_document_batches/:documentBatchId",
      component: ElmRoute,
      onEnter: requireAuth
    },
    {
      path: "/readonly_emails/:emailId",
      component: ElmRoute,
      onEnter: requireAuth
    },
    {
      path: "/index-document-batches/:batchId/factor_invoices/:factorInvoiceId",
      component: FactorInvoiceExtractDataPage,
      onEnter: requireAuth
    },
    { path: "/login", component: LoginForm },
    { path: "/forwarding-has-moved", component: ElmRoute },
    { path: "/document_reviews/:reviewType/:reviewId", component: ElmRoute },
    {
      path: "/document_batch_reviews/:reviewType/:reviewId",
      component: ElmRoute
    },
    {
      path: "/document_group_reviews/:reviewType/:reviewId",
      component: ElmRoute
    },
    { path: "/logout", component: Logout, onEnter: requireAuth },
    {
      path: "/index-document-batches/:batchId/loads/:loadId/carrier_invoices/:carrierInvoiceId",
      component: ConfirmAndSubmitPage,
      onEnter: requireAuth
    },
    {
      path: [
        "/loads/:loadId",
        "/loads/:loadId/carrier_invoices/:carrierInvoiceId"
      ],
      component: ConfirmAndSubmitPage,
      onEnter: requireAuth
    },
    { path: "/settings", redirectTo: settingsPath },
    { path: "/settings/carriers", component: ElmRoute, onEnter: requireAuth },
    {
      path: "/settings/carriers/:carrierId",
      component: ElmRoute,
      onEnter: requireAuth
    },
    {
      path: "/settings/carriers/:id/documents",
      component: ElmRoute,
      onEnter: requireAuth
    },
    { path: "/settings/account", component: ElmRoute, onEnter: requireAuth },
    {
      path: "/settings/client_portal",
      component: ElmRoute,
      onEnter: requireAuth
    },
    { path: "/settings/contacts", component: ElmRoute, onEnter: requireAuth },
    {
      path: ["/settings/customers", "/settings/customers/:id"],
      component: ElmRoute,
      onEnter: requireAuth
    },
    {
      path: ["/settings/debtors", "/settings/debtors/:id"],
      component: ElmRoute,
      onEnter: requireAuth
    },
    {
      path: "/settings/document_types",
      component: ElmRoute,
      onEnter: requireAuth
    },
    { path: "/settings/exceptions", component: ElmRoute, onEnter: requireAuth },
    {
      path: "/settings/integrations",
      component: ElmRoute,
      onEnter: requireAuth
    },
    {
      path: "/settings/next_gen_audit",
      component: ElmRoute,
      onEnter: requireAuth
    },
    { path: "/settings/rules", component: ElmRoute, onEnter: requireAuth },
    {
      path: "/settings/reference_fields",
      component: ElmRoute,
      onEnter: requireAuth
    },
    { path: "/settings/reports", component: ElmRoute, onEnter: requireAuth },
    {
      path: ["/settings/users", "/settings/users/:id"],
      component: ElmRoute,
      onEnter: requireAuth
    },
    {
      path: "/settings/users/:id/enable",
      component: ElmRoute,
      onEnter: requireAuth
    },
    {
      path: "/settings/verifications",
      component: ElmRoute,
      onEnter: requireAuth
    },
    {
      path: "/settings/scraper_status",
      component: ElmRoute,
      onEnter: requireAuth
    },
    {
      path: "/settings/pod_definition",
      component: ElmRoute,
      onEnter: requireAuth
    },
    { path: "/forwarding/*", component: ElmRoute, onEnter: requireAuth },
    {
      path: "/verification_batch/*",
      component: ElmRoute,
      onEnter: requireAuth
    },
    {
      path: "/index-document-batches/:documentBatchId/loads/:loadStateId/confirm",
      component: ElmRoute,
      onEnter: requireAuth
    },
    { path: "/email/:id", component: EmailPage, onEnter: requireAuth },
    { path: "/email_merge", component: ElmRoute, onEnter: requireAuth },
    { path: "/reply/:id", component: ReplyView, onEnter: requireAuth },
    { path: "/terms", component: ElmRoute, onEnter: requireAuth },
    {
      path: "/customer_representative",
      component: externalApp("/customer_representative")
    },
    {
      path: "/insights_dashboard",
      component: externalApp("/insights_dashboard")
    },
    {
      path: "/loads/:loadId/carrier_invoices/:carrierInvoiceId/ltl-reconcile",
      component: ElmRoute,
      onEnter: requireAuth
    },
    { path: "/held_submissions", component: ElmRoute, onEnter: requireAuth },
    { path: "/debtor_changes", component: ElmRoute, onEnter: requireAuth },
    {
      path: "/debtor_changes/templates/:id",
      component: ElmRoute,
      onEnter: requireAuth
    },
    { path: "/my_clients_demo", component: ElmRoute, onEnter: requireAuth },
    { path: "/noa_management", component: ElmRoute, onEnter: requireAuth },
    { path: "/noa_management/sent", component: ElmRoute, onEnter: requireAuth },
    {
      path: "/noa_management/skipped",
      component: ElmRoute,
      onEnter: requireAuth
    },
    { path: "/analytics", component: AnalyticsPage, onEnter: requireAuth },
    { path: "*", redirectTo: "/" }
  ];
  return /* @__PURE__ */ jsx(LegacyRouter, { history, layout: App, routes });
}
